import { useState, useRef, useEffect } from 'react'
import { Circle, Plus, X } from 'lucide-react'
import Tooltip from './Tooltip'
import { TargetDataType, useTarget } from '../../context/TargetContext'

type TargetsProps = {
    id: string | number
    name: string
    color: string
}

const Targets = ({ values, mode }: { values: TargetsProps[]; mode: string }) => {
    const { targets: targetsContext } = useTarget()
    const [data, setData] = useState<TargetDataType[]>([])
    const [selectedTargets, setSelectedTargets] = useState<TargetDataType[]>([])
    const [showDropdown, setShowDropdown] = useState(false)
    const [dropdownAlignRight, setDropdownAlignRight] = useState(true)
    const buttonRef = useRef<HTMLButtonElement | null>(null)
    const dropdownRef = useRef<HTMLDivElement | null>(null)

    const addTarget = (target: TargetDataType) => {
        if (!selectedTargets.some(t => t.id === target.id)) {
            setSelectedTargets([...selectedTargets, target])
        }
        setShowDropdown(false)
    }

    const removeTarget = (id: number | string) => {
        setSelectedTargets(selectedTargets.filter(target => target.id !== id))
    }

    const handleClickOutside = (event: MouseEvent) => {
        if (
            buttonRef.current &&
            !buttonRef.current.contains(event.target as Node) &&
            dropdownRef.current &&
            !dropdownRef.current.contains(event.target as Node)
        ) {
            setShowDropdown(false)
        }
    }

    useEffect(() => {
        if (showDropdown) {
            document.addEventListener('mousedown', handleClickOutside)
        } else {
            document.removeEventListener('mousedown', handleClickOutside)
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [showDropdown])

    useEffect(() => {
        if (showDropdown && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect()
            const spaceRight = window.innerWidth - rect.right
            const spaceLeft = rect.left

            setDropdownAlignRight(selectedTargets.length === 0 || spaceRight > spaceLeft)
        }
    }, [showDropdown, selectedTargets])

    useEffect(() => {
        if (targetsContext) setData(targetsContext) // Atualiza `data` com as targets do contexto.
    }, [targetsContext])

    // Carrega etiquetas
    useEffect(() => {
        if (values) {
            setSelectedTargets(values)
        }
    }, [values])

    return (
        <>
            {data && (
                <div>
                    {/* <h4 className='mb-1 font-bold'>Etiquetas</h4> */}
                    <div className=''>
                        <div className='flex flex-wrap gap-1 items-center'>
                            {selectedTargets.map(target => (
                                <span
                                    key={target.id}
                                    className='flex items-center gap-1 px-4 py-2 rounded-xl text-gray-800 dark:text-gray-200'
                                    style={{ border: `2px solid ${target.color}` }}
                                >
                                    <p className='font-medium'>{target.name}</p>
                                    {mode === 'edit' && (
                                        <X
                                            size={14}
                                            className='cursor-pointer text-gray-400 hover:text-gray-500 dark:text-gray-500 hover:dark:text-gray-400'
                                            onClick={() => removeTarget(target.id)}
                                        />
                                    )}
                                </span>
                            ))}
                            <div className='relative'>
                                {mode === 'edit' && (
                                    <Tooltip label='Adicionar etiqueta' placement='topright'>
                                        <button
                                            ref={buttonRef}
                                            className='flex items-center px-3 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 hover:dark:bg-gray-600 border dark:border-gray-600 rounded-xl'
                                            onClick={() => setShowDropdown(!showDropdown)}
                                        >
                                            <Plus size={16} />
                                        </button>
                                    </Tooltip>
                                )}
                                {showDropdown && (
                                    <div
                                        ref={dropdownRef}
                                        className={`absolute mt-1 w-40 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-xl shadow-md z-50 ${
                                            dropdownAlignRight ? 'left-0' : 'right-0'
                                        }`}
                                    >
                                        {data.map(target => (
                                            <div
                                                key={target.id}
                                                className='flex items-center px-4 py-2 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700'
                                                onClick={() => addTarget(target)}
                                            >
                                                <Circle
                                                    className='mr-2 flex-shrink-0'
                                                    size={12}
                                                    fill={target.color}
                                                    color={target.color}
                                                />
                                                <span className='truncate font-normal'>{target.name}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    )
}

export default Targets
